import { getImagesForBlock } from "block-system/blocks/__shared__/images";
import { LayoutItemColumnSpan } from "../Block/constants";
import { LayoutAllowedItemTypes, LayoutItem } from "../schema";
import { LayoutBlock } from "../types";
import cuid from "cuid";

export function getOrderedLayoutItems(block: LayoutBlock): LayoutItem[] {
  const layoutItemsWithoutPlaceholder: LayoutItem[] = block.children
    .map((item, index) => ({
      name: getImagesForBlock(item.type).thumbnail.name,
      block: item,
      index,
      order: block.config.ordering[item.config.id as string],
    }))
    .sort((a, b) => a.order - b.order);

  const layoutItems: LayoutItem[] = [];

  const hasBlockSpanningTwoColumns = layoutItemsWithoutPlaceholder.some(
    (layoutItem) => isItemOfColSpan2(layoutItem, block.config.columnCount)
  );

  const layoutItemsCount = hasBlockSpanningTwoColumns
    ? block.config.columnCount - 1
    : block.config.columnCount;

  for (let i = 0; i < layoutItemsCount; i++) {
    const item = layoutItemsWithoutPlaceholder.find(
      (item) =>
        item.order === i ||
        (item.order === block.config.columnCount &&
          block.config.columnCount === 2)
    );
    if (item) {
      layoutItems.push(item);
    } else {
      layoutItems.push({ name: "Empty", block: null, index: i, order: i });
    }
  }
  return layoutItems;
}

export function getLayoutItemColSpan(
  blockType: LayoutAllowedItemTypes,
  maxColumnCount: number
) {
  if (maxColumnCount === 3) {
    return LayoutItemColumnSpan[blockType].threeColumn;
  }

  return LayoutItemColumnSpan[blockType].twoColumn;
}

export const INVALID_LAYOUT_ITEM_INSERT_POSITION = -1;

export function getLayoutItemInsertPosition(
  block: {
    config: Pick<LayoutBlock["config"], "ordering" | "columnCount">;
    children: LayoutBlock["children"];
  },
  insertBlockType: LayoutAllowedItemTypes,
  insertPosition: number
) {
  const currentOrdering = block.config.ordering;
  const currentOrderingIndices = Object.values(currentOrdering);
  const maxColumnCount = block.config.columnCount;

  const isFreeColumn = !currentOrderingIndices.includes(insertPosition);
  const isInvalidInsertPosition =
    insertPosition < 0 || insertPosition >= maxColumnCount;
  if (isInvalidInsertPosition || !isFreeColumn) {
    return INVALID_LAYOUT_ITEM_INSERT_POSITION;
  }

  const itemColSpan = getLayoutItemColSpan(insertBlockType, maxColumnCount);
  if (itemColSpan === 1) {
    const numOccupiedColumns = block.children.reduce((acc, child) => {
      return acc + getLayoutItemColSpan(child.type, maxColumnCount);
    }, 0);

    const hasUnoccupiedColumns =
      numOccupiedColumns + itemColSpan <= maxColumnCount;

    if (hasUnoccupiedColumns) {
      return insertPosition;
    }
  }

  if (itemColSpan === 2) {
    // We need to get the column span of previous item (if it exists) to make sure
    // that the previous item does not occupy the insertPosition.
    const prevItemId = Object.entries(currentOrdering).find(
      ([, order]) => order === insertPosition - 1
    )?.[0];

    const prevItem = block.children.find(
      (item) => item.config.id === prevItemId
    );

    const isPrevItemColSpan2 = prevItem
      ? getLayoutItemColSpan(prevItem.type, maxColumnCount) === 2
      : false;

    const hasAvailableSpaceToTheRight =
      insertPosition + 1 < maxColumnCount &&
      !currentOrderingIndices.includes(insertPosition + 1) &&
      !isPrevItemColSpan2;

    const hasAvailableSpaceToTheLeft =
      insertPosition - 1 >= 0 &&
      !currentOrderingIndices.includes(insertPosition - 1);

    if (hasAvailableSpaceToTheRight) {
      return insertPosition;
    } else if (hasAvailableSpaceToTheLeft) {
      return insertPosition - 1;
    }
  }

  return INVALID_LAYOUT_ITEM_INSERT_POSITION;
}

export function getAllowedItemsForPosition(
  block: LayoutBlock,
  position: number
): LayoutAllowedItemTypes[] {
  const allowedItems = Object.keys(LayoutItemColumnSpan).filter((item) => {
    return (
      getLayoutItemInsertPosition(
        block,
        item as LayoutAllowedItemTypes,
        position
      ) !== INVALID_LAYOUT_ITEM_INSERT_POSITION
    );
  });

  return allowedItems as LayoutAllowedItemTypes[];
}

function isItemOfColSpan2(item: LayoutItem, maxColumnCount: number) {
  return Boolean(
    item.block && getLayoutItemColSpan(item.block.type, maxColumnCount) === 2
  );
}

/**
 * This function "replays" the insertions of the children blocks into the layout block.
 * This allows us to validate whether the ordering configuration is valid.
 *
 * If, by "replaying" the insertions, we can't insert a block at a given position,
 * then the ordering configuration is invalid.
 *
 * Doing this is much easier than writing validation function that works on the ordering configuration directly.
 *
 * Currently, we use this function in the context of Copilot.
 * We might want to "promote" it to "update page flow" validation code later.
 */
export function assertLayoutBlockOrdering(block: LayoutBlock) {
  const {
    children,
    config: { ordering, columnCount },
  } = block;

  const hasChildren = children.length > 0;
  const hasOrdering = Object.keys(ordering).length > 0;

  if (!hasChildren && hasOrdering) {
    const orderingIds = Object.keys(ordering);
    throw new Error(
      `All children must be included in the ordering configuration. Missing: ${orderingIds.join(", ")}`
    );
  }

  if (hasChildren && !hasOrdering) {
    const firstChildId = children[0].config.id;
    throw new Error(
      `Child block ${children[0].type} (${firstChildId}) is not defined in the ordering configuration.`
    );
  }

  const childIds = new Set(children.map((child) => child.config.id));
  const mismatchedChildIds = Object.keys(ordering).filter(
    (childId) => !childIds.has(childId)
  );

  if (mismatchedChildIds.length > 0) {
    throw new Error(
      `All children must be included in the ordering configuration. Missing: ${mismatchedChildIds.join(", ")}`
    );
  }

  const currentOrdering: LayoutBlock["config"]["ordering"] = {};
  const currentChildren: LayoutBlock["children"] = [];

  for (const child of children) {
    const childId = child.config.id;

    if (!childId || !cuid.isCuid(childId)) {
      throw new Error(
        "Child blocks of Layout block must have a valid ID that is not a placeholder."
      );
    }

    const childOrder = ordering[childId];
    if (childOrder == null) {
      throw new Error(
        `Child block ${child.type} (${childId}) is not defined in the ordering configuration.`
      );
    }

    const insertPosition = getLayoutItemInsertPosition(
      {
        config: {
          ordering: currentOrdering,
          columnCount,
        },
        children: currentChildren,
      },
      child.type,
      childOrder
    );

    if (insertPosition === INVALID_LAYOUT_ITEM_INSERT_POSITION) {
      throw new Error(
        `Invalid ordering. Block ${child.type} (${childId}) can't have an ordering of ${childOrder}. Potential issues:
          - The ordering 'slot' is already occupied.
          - There are not enough room to add the block. Keep in mind that some blocks occupy more than one column if the Layout block has three columns.
          - The block is not valid. It can't be inserted at the given position.`
      );
    }

    currentChildren.push(child);
    currentOrdering[childId] = insertPosition;
  }
}
